/**
 * Shared data-fetching hooks for the EEG Customization Workspace.
 */
import { useCallback, useEffect, useState } from "react";
import apiClient from "@shared/services/apiClient";
import { getErrorMessage } from "./customizationUtils";

// ---------------------------------------------------------------------------
// Montage options
// ---------------------------------------------------------------------------

export type MontageOption = {
  montage_id: string;
  montage_name: string;
};

/**
 * Fetches the full list of montages (system + user-owned).
 * Returns a stable `options` array plus a `loading` flag.
 */
export function useMontageOptions(): {
  options: MontageOption[];
  loading: boolean;
  error: string | null;
} {
  const [options, setOptions] = useState<MontageOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get("/montages")
      .then((res) => {
        if (cancelled) return;
        const raw = (res as { data?: unknown })?.data;
        const payload =
          raw &&
          typeof raw === "object" &&
          "data" in (raw as Record<string, unknown>)
            ? (raw as { data: unknown }).data
            : raw;
        const list = Array.isArray(payload) ? (payload as MontageOption[]) : [];
        setOptions(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading, error };
}

// ---------------------------------------------------------------------------
// Recording channel names
// ---------------------------------------------------------------------------

/**
 * Fetches the channel list for a given EEG recording.
 * Returns [] while `recordingId` is blank.
 */
export function useRecordingChannels(recordingId: string): {
  channels: string[];
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [channels, setChannels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!recordingId.trim()) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get(`/eeg/recordings/${recordingId.trim()}`)
      .then((res) => {
        if (cancelled) return;
        const raw = (res as { data?: unknown })?.data;
        const payload =
          raw &&
          typeof raw === "object" &&
          "data" in (raw as Record<string, unknown>)
            ? (raw as { data: unknown }).data
            : raw;
        const names = Array.isArray(
          (payload as { channel_names?: unknown })?.channel_names,
        )
          ? (payload as { channel_names: string[] }).channel_names
          : [];
        setChannels(names);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recordingId, tick]);

  return { channels, loading, error, reload };
}
